import { hasUnsavedFormData } from '../../../shared/forms/useUnsavedBrowserNavigationGuard';

const MEANINGFUL_PARENT_FINANCIAL_AID_IGNORED_KEYS = new Set([
  'id',
  'documents',
  'signatures',
]);

const readText = (value: unknown): string => String(value ?? '').trim();

const stableJsonValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map((entry) => stableJsonValue(entry));
  if (!value || typeof value !== 'object') return value ?? null;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, stableJsonValue(entry)]),
  );
};

const stableFingerprint = (value: unknown): string => {
  try {
    return JSON.stringify(stableJsonValue(value));
  } catch {
    return '';
  }
};

export const resolveParentFinancialAidApplicationHydrationKey = (application: unknown): string => {
  const record = application && typeof application === 'object'
    ? application as Record<string, unknown>
    : {};
  const id = readText(record.id);
  if (!id) return '';
  const updatedAt = readText(record.updatedAt) || readText(record.submittedAt) || 'initial';
  return [
    id,
    updatedAt,
    stableFingerprint(record.sections),
    stableFingerprint(record.hydration),
  ].join(':');
};

export const countMeaningfulParentFinancialAidFields = (value: unknown, key = ''): number => {
  if (MEANINGFUL_PARENT_FINANCIAL_AID_IGNORED_KEYS.has(key)) return 0;
  if (typeof value === 'string') return value.trim() ? 1 : 0;
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0 ? 1 : 0;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (!value || typeof value !== 'object') return 0;
  if (Array.isArray(value)) {
    return value.reduce((sum, entry) => sum + countMeaningfulParentFinancialAidFields(entry), 0);
  }
  return Object.entries(value as Record<string, unknown>).reduce(
    (sum, [childKey, childValue]) => sum + countMeaningfulParentFinancialAidFields(childValue, childKey),
    0,
  );
};

export const stripSystemManagedFinancialAidDocuments = <T extends object>(data: T): T => ({
  ...data,
  documents: [],
});

export const hasHydrationBlockingUnsavedParentFinancialAidData = (
  current: object,
  initial: object,
): boolean => hasUnsavedFormData(
  stripSystemManagedFinancialAidDocuments(current),
  stripSystemManagedFinancialAidDocuments(initial),
);

export const shouldPreserveMoreCompleteLocalFinancialAidData = (
  previous: object,
  initial: object,
  hydrated: object,
): boolean => {
  const previousCount = countMeaningfulParentFinancialAidFields(previous);
  const initialCount = countMeaningfulParentFinancialAidFields(initial);
  const hydratedCount = countMeaningfulParentFinancialAidFields(hydrated);
  return previousCount > initialCount && hydratedCount < previousCount;
};

const hasAttestationEntries = (value: Record<string, boolean> | null | undefined): boolean =>
  Boolean(value && Object.keys(value).length > 0);

/**
 * Resolve the attestation checklist for a rebuilt (hydrated) parent form.
 *
 * The persisted draft snapshot currently loses `signatures.attestations`
 * server-side, so any form rebuild from the application record would clear
 * the final-page agreement checklist while every other field round-trips
 * (the INT-636 blocked-submit QA finding). Preference order:
 * 1. live in-session attestation state (any key present means the parent
 *    interacted with the checklist this session, including unchecking),
 * 2. attestations restored from the application record itself,
 * 3. attestations captured in browser storage at the last submit attempt.
 */
export const resolveHydratedParentFinancialAidAttestations = (
  hydratedAttestations: Record<string, boolean>,
  previousAttestations: Record<string, boolean> | null | undefined,
  storedAttestations: Record<string, boolean> | null | undefined,
): Record<string, boolean> => {
  if (hasAttestationEntries(previousAttestations)) return { ...previousAttestations };
  if (hasAttestationEntries(hydratedAttestations)) return hydratedAttestations;
  if (hasAttestationEntries(storedAttestations)) return { ...storedAttestations };
  return hydratedAttestations;
};
